import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import {
  degreeCardDetailedVariants,
  type DegreeCardDetailedVariantProps,
} from './degree-card-detailed.variants';
import type { IconName } from '@/hen-ui/atomics';
import {
  Badge,
  Button,
  Heading,
  Icon,
  Image,
  LineClamp,
  Link,
  List,
  ListItem,
  Text,
  type BadgeVariantProps,
} from '@/hen-ui/atomics';
import { Price } from '@/hen-ui/atomics/Price';
import { CardRoot, type CardLinkTarget } from '@/hen-ui/composites/Card/Card';
import {
  MINIMAL_MARKDOWN_LINK_CLASS_NAME,
  parseMinimalMarkdown,
} from '@/hen-ui/lib/minimal-markdown';
import {
  Card,
  CardContent,
  CardFooter,
  CheckboxButton,
  Flex,
  Grid,
  LogoBox,
  PropertyValue,
  Rating,
} from '@/hen-ui/composites';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { useExpandableClamp } from '@/hen-ui/hooks';
import type {
  DegreeCardOptionalCtaProps,
  DegreeCardReadMoreProps,
} from './types';

/** Default disclaimer copy when rendering the trusted-partner footer strip. */
export const DEGREE_CARD_DEFAULT_TRUSTED_PARTNER_TEXT =
  'You will be taken to a trusted partner site';

/** Matches CheckboxButton sm typography; overrides shadcn Button `text-sm` on the CTA. */
const DEGREE_CARD_CTA_TYPOGRAPHY = '!text-b5 !leading-b5';

export interface DegreeCardDetailedDataPoint {
  property: string;
  value: string;
}

export interface DegreeCardDetailedDetailItem {
  icon: IconName;
  label: string;
}

/** How a detail row label is drawn. */
export const DEGREE_CARD_DETAIL_LIST_FORMAT = ['plain', 'markdown'] as const;

export type DegreeCardDetailListFormat =
  (typeof DEGREE_CARD_DETAIL_LIST_FORMAT)[number];

/** Where the detail rows sit in the card body. */
export const DEGREE_CARD_DETAIL_LIST_POSITION = ['default', 'bottom'] as const;

export type DegreeCardDetailListPosition =
  (typeof DEGREE_CARD_DETAIL_LIST_POSITION)[number];

export type DegreeCardDetailedProps = React.HTMLAttributes<HTMLDivElement> &
  DegreeCardDetailedVariantProps &
  DegreeCardReadMoreProps &
  DegreeCardOptionalCtaProps & {
    /** Compare checkbox in footer alongside primary CTA. Default: true */
    compare?: boolean;
    /** Controlled checked state for the Compare checkbox. */
    compareChecked?: boolean;
    /** Called when the Compare checkbox checked state changes. */
    onCompareChange?: (checked: boolean) => void;
    /** When true, the Compare control is visible but not interactive (e.g. max selections reached). */
    compareDisabled?: boolean;
    /** Heart icon in header for save/wishlist. Default: false */
    favorite?: boolean;
    /** Called when the favorite (heart) button is clicked. Use with favorite=true to wire up. */
    onFavoriteClick?: () => void;
    schoolLogo: string;
    /**
     * Institution or provider name shown in the school header row.
     * Optional when the logo/image header already identifies the school, or when
     * the provider line is passed via `subtitle` (e.g. product-card layouts).
     */
    schoolName?: string;
    schoolDetails?: string;
    degreeTitle: string;
    /**
     * When true, the program name stays on two lines and the rest is hidden.
     * Off by default so existing cards show the full title.
     */
    truncateTitle?: boolean;
    /**
     * Optional line directly under `degreeTitle` (e.g. provider name).
     * Body B4; rendered before tags.
     */
    subtitle?: string;
    tags?: string[];
    /**
     * Visual style for skill/category tags. Defaults to `"tag"` (neutral chip).
     * Use `"primary"` for filled brand pills (e.g. product-card layouts).
     */
    tagVariant?: NonNullable<BadgeVariantProps['variant']>;
    /**
     * Body copy below tags. Omitted when empty or unset (product-card layouts
     * typically use `subtitle` / `subDescription` instead).
     */
    description?: string;
    /**
     * Optional secondary line under `subtitle` (e.g. enrollment count).
     * Body B4; rendered with `subtitle` before tags. Omitted when empty.
     */
    subDescription?: string;
    /**
     * Optional heading rendered above the description block.
     * Pairs with `listItems` to label a bulleted snippet (e.g. "Why Consider This Program").
     * When `listItems` is empty, this heading is not rendered.
     */
    listHeading?: string;
    /**
     * When provided (non-empty), renders these as a checked-bullet list instead of the paragraph
     * description. Useful when the snippet is best presented as discrete reasons rather than prose.
     * Pass already-prepared nodes (e.g. spans with keyword bolding); the composite stays presentation-only.
     */
    listItems?: React.ReactNode[];
    dataPointsList?: DegreeCardDetailedDataPoint[];
    /**
     * Icon-and-text rows in the card body. The first three are shown.
     * Price, when set, stays under them.
     */
    detailListItems?: DegreeCardDetailedDetailItem[];
    /**
     * Where the detail rows sit. `default` keeps them in flow under the
     * facts. `bottom` pins them to the bottom of the card, just above the
     * footer. An empty value keeps the default place.
     */
    detailListItemsPosition?: DegreeCardDetailListPosition | '';
    /**
     * How detail row labels are drawn. `plain` prints them as typed.
     * `markdown` allows `**bold**`, `*italic*`, and `[text](url)`, the same
     * limited set as chart tooltips.
     */
    detailListItemsFormat?: DegreeCardDetailListFormat;
    /**
     * Current price shown below the detail list (e.g. `"$120"`).
     * Pass a pre-formatted string — the card does not format currency.
     */
    price?: string;
    /**
     * Optional former price with strikethrough, paired with `price`
     * (e.g. `"$150"`). Ignored when `price` is empty.
     */
    originalPrice?: string;
    /**
     * Star rating shown under the title block (e.g. `4.5`).
     * Omit, `null`, or a non-finite value to hide the rating row.
     */
    ratingValue?: number | null;
    /**
     * Integer review count shown beside the score (e.g. `8,134`).
     * Ignored when `ratingValue` is hidden.
     */
    reviewAmount?: number;
    imageHeaderSrc?: string;
    superlativeLabel?: string;
    superlativeBadgeLabel?: string;
    /** When set to a non-empty string, renders lock + disclaimer at bottom. */
    trustedPartnerText?: string;
    /**
     * Footer control layout. `row` (default) places Compare and CTA side by side;
     * `column` stacks them full-width; `stackUntilSm` stacks below 640px then rows at sm+.
     */
    footerLayout?: 'row' | 'column' | 'stackUntilSm';
    /** Makes the entire card clickable using the specified URL. */
    href?: string;
    /** Opens the card link in the same tab or a new tab. */
    target?: CardLinkTarget;
    /**
     * Name read by screen readers when the card is a link. Left empty, they
     * read all of the card's text.
     */
    ariaLabel?: string;
  };

/**
 * DegreeCardDetailed — most configurable variant (large radius).
 * Optional sections render when their data is present (data points, detail list, image header, trusted partner).
 * Reuse: Card, Badge, Button, CheckboxButton, Image, Icon, Link, PropertyValue, Heading, Text, Price, Rating.
 */
export const DegreeCardDetailed = React.forwardRef<
  HTMLDivElement,
  DegreeCardDetailedProps
>(
  (
    {
      compare = true,
      favorite = false,
      readMoreBehavior = 'expand',
      schoolLogo,
      schoolName,
      schoolDetails,
      degreeTitle,
      truncateTitle = false,
      subtitle,
      tags,
      tagVariant = 'tag',
      description = '',
      subDescription,
      listHeading,
      listItems,
      readMoreHref,
      dataPointsList = [],
      detailListItems = [],
      detailListItemsFormat = 'plain',
      detailListItemsPosition = 'default',
      price,
      originalPrice,
      ratingValue,
      reviewAmount,
      ctaLabel,
      ctaHref,
      onCtaClick,
      imageHeaderSrc,
      superlativeLabel,
      superlativeBadgeLabel,
      trustedPartnerText,
      onFavoriteClick,
      compareChecked,
      onCompareChange,
      compareDisabled = false,
      footerLayout = 'row',
      href,
      target,
      ariaLabel,
      className,
      border,
      ...props
    },
    ref,
  ) => {
    const useExpand = readMoreBehavior === 'expand';
    const descriptionId = React.useId();
    const hasBulletList = Boolean(listItems && listItems.length > 0);
    // `useExpandableClamp` resets when its `content` string changes. The
    // bullet branch composes a signal from the list length plus the paragraph
    // fallback so a new item set (different snippet, different bullet count)
    // still collapses on remount or re-render, without coupling expand-reset
    // to the paragraph string alone.
    const expandSignal = hasBulletList
      ? `bullets:${listItems!.length}:${description}`
      : description;
    const {
      ref: descriptionRef,
      expanded: descriptionExpanded,
      isTruncated: isDescriptionTruncated,
      toggleExpanded,
    } = useExpandableClamp(expandSignal);

    const dataPointsSlice = Array.isArray(dataPointsList)
      ? dataPointsList.slice(0, 3)
      : [];
    const detailListSlice = Array.isArray(detailListItems)
      ? detailListItems.slice(0, 3)
      : [];
    const pinDetailList = detailListItemsPosition === 'bottom';

    // Bullet list mode renders only the first item in the collapsed state and
    // expands to show every item once Read More is clicked. The Read More
    // affordance appears whenever there is more than one bullet, so we don't
    // need overflow measurement for the bullet branch.
    const hasMultipleBullets = hasBulletList && (listItems?.length ?? 0) > 1;
    const collapseBulletList = !useExpand || !descriptionExpanded;
    const visibleBulletItems = hasBulletList
      ? collapseBulletList && hasMultipleBullets
        ? listItems!.slice(0, 1)
        : listItems!
      : [];

    const showImageHeaderBlock = Boolean(
      (imageHeaderSrc && imageHeaderSrc.trim()) ||
        (superlativeLabel && superlativeLabel.trim()) ||
        (superlativeBadgeLabel && superlativeBadgeLabel.trim()),
    );

    const showTrustedPartnerStrip = Boolean(trustedPartnerText?.trim());
    const isStackedFooter = footerLayout === 'column';
    const isStackUntilSmFooter = footerLayout === 'stackUntilSm';
    const isNarrowStackedFooter = isStackedFooter || isStackUntilSmFooter;
    const hasSchoolName = Boolean(schoolName?.trim());
    const hasSchoolDetails = Boolean(schoolDetails?.trim());
    const showSchoolTextColumn = hasSchoolName || hasSchoolDetails;
    const showInlineLogo = !showImageHeaderBlock;
    const showSchoolHeaderRow =
      showInlineLogo || showSchoolTextColumn || favorite;
    const logoAlt = hasSchoolName ? `${schoolName} logo` : 'School logo';
    const hasSubtitle = Boolean(subtitle?.trim());
    const hasSubDescription = Boolean(subDescription?.trim());
    const showTitleMeta = hasSubtitle || hasSubDescription;
    const hasDescription = Boolean(description?.trim());
    const showCta =
      Boolean(ctaLabel?.trim()) && Boolean(ctaHref?.trim() || onCtaClick);
    const showRating = ratingValue != null && Number.isFinite(ratingValue);
    const formattedReviewCount =
      reviewAmount != null &&
      Number.isInteger(reviewAmount) &&
      reviewAmount >= 0
        ? reviewAmount.toLocaleString('en-US')
        : undefined;
    const hasCardLink = Boolean(href?.trim());
    const Shell = hasCardLink ? Card : CardRoot;

    return (
      <Shell
        ref={ref}
        href={href}
        target={target}
        ariaLabel={ariaLabel}
        className={cn(degreeCardDetailedVariants({ border }), className)}
        data-hen-id={COMPONENT_IDS.DegreeCardDetailed}
        {...props}
      >
        <CardContent
          padding='p-sm'
          className={pinDetailList ? 'min-h-0 flex-1' : undefined}
        >
          <Flex
            direction='column'
            gap='sm'
            className={pinDetailList ? 'min-h-0 w-full flex-1' : undefined}
          >
            {showImageHeaderBlock && (
              <Card
                variant='background-transparent'
                border='none'
                rounded='md'
                trackable={false}
                className='overflow-hidden'
              >
                <Flex
                  direction='row'
                  align='center'
                  justify='between'
                  className='w-full shrink-0 rounded-t-md bg-neutral-200 px-sm py-xxs'
                >
                  <Flex direction='row' align='center' gap='xs'>
                    <Icon
                      icon='book-open'
                      size='sm'
                      className='text-body'
                      aria-hidden
                    />
                    {superlativeLabel && (
                      <Text variant='l2' color='default'>
                        {superlativeLabel}
                      </Text>
                    )}
                  </Flex>
                  {superlativeBadgeLabel && (
                    <Badge variant='secondaryFill'>
                      {superlativeBadgeLabel}
                    </Badge>
                  )}
                </Flex>
                <div className='relative h-degree-card-header w-full rounded-b-md bg-neutral-200 px-md py-lg'>
                  {imageHeaderSrc && (
                    <Image
                      src={imageHeaderSrc}
                      alt=''
                      className='absolute inset-0 h-full w-full object-cover'
                    />
                  )}
                  <Flex
                    direction='row'
                    align='center'
                    className='relative h-full'
                  >
                    <LogoBox
                      src={schoolLogo}
                      alt={logoAlt}
                      className='[&_img]:p-1'
                    />
                  </Flex>
                </div>
              </Card>
            )}
            {showSchoolHeaderRow && (
              <Flex direction='row' align='start' justify='between' gap='md'>
                <Flex direction='row' align='center' gap='sm'>
                  {showInlineLogo && (
                    <LogoBox
                      src={schoolLogo}
                      alt={logoAlt}
                      className='[&_img]:p-1'
                    />
                  )}
                  {showSchoolTextColumn && (
                    <Flex direction='column' gap='xxs'>
                      {hasSchoolName && (
                        <Heading variant='h4' color='default'>
                          {schoolName}
                        </Heading>
                      )}
                      {hasSchoolDetails && (
                        <Text variant='b4' color='default'>
                          {schoolDetails}
                        </Text>
                      )}
                    </Flex>
                  )}
                </Flex>
                {favorite && (
                  <Button
                    variant='iconOnly'
                    size='sm'
                    iconBefore='heart'
                    aria-label='Add to favorites'
                    onClick={onFavoriteClick}
                  />
                )}
              </Flex>
            )}

            <Flex direction='column' gap='xxs'>
              <Heading
                variant='h4'
                color='default'
                className={
                  truncateTitle
                    ? 'break-words line-clamp-2 overflow-hidden text-ellipsis'
                    : undefined
                }
              >
                {degreeTitle}
              </Heading>
              {showTitleMeta && (
                <Flex direction='column' gap='min'>
                  {hasSubtitle && (
                    <Text variant='b4' color='default' tag='p'>
                      {subtitle}
                    </Text>
                  )}
                  {hasSubDescription && (
                    <Text variant='b4' color='default' tag='p'>
                      {subDescription}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>

            {showRating && (
              <Rating
                variant='rating-star'
                rating={ratingValue}
                ratingCount={formattedReviewCount}
                showRatingCount={Boolean(formattedReviewCount)}
                ratingCountAriaSuffix={
                  formattedReviewCount ? ' reviews' : undefined
                }
              />
            )}

            {tags && tags.length > 0 && (
              <Flex direction='row' gap='xs' wrap='wrap'>
                {tags.map((tag, index) => (
                  <Badge key={`${index}-${tag}`} variant={tagVariant}>
                    {tag}
                  </Badge>
                ))}
              </Flex>
            )}

            {hasBulletList ? (
              <>
                {listHeading && (
                  <Text variant='b4' color='default' weight='semibold' tag='p'>
                    {listHeading}
                  </Text>
                )}
                <div id={descriptionId}>
                  {/*
                    Bullets render with `bulletStyle='none'` and a composite
                    inline check icon so the marker uses secondary-700 (the
                    shared `List` check style hardcodes primary-700). The
                    layout mirrors `ListItem`'s built-in check row.
                  */}
                  <List bulletStyle='none'>
                    {visibleBulletItems.map((item, index) => (
                      <ListItem key={index} className='flex items-start gap-2'>
                        <span
                          className='flex h-text-line shrink-0 items-center'
                          aria-hidden
                        >
                          <Icon
                            icon='circle-check'
                            size='sm'
                            color='secondary'
                            className='translate-y-px'
                          />
                        </span>
                        <span className='min-w-0 flex-1'>
                          {/*
                            When the list is collapsed and there is more than
                            one item, only the first bullet renders as a
                            teaser. Clamp it to 2 lines so a long lead
                            sentence still produces a consistent preview
                            height. Expanded items render in full so every
                            reason is visible after Read More.
                          */}
                          {collapseBulletList && hasMultipleBullets ? (
                            <LineClamp lines={2}>{item}</LineClamp>
                          ) : (
                            item
                          )}
                        </span>
                      </ListItem>
                    ))}
                  </List>
                </div>
              </>
            ) : (
              hasDescription && (
                <LineClamp
                  ref={descriptionRef}
                  id={descriptionId}
                  lines={useExpand ? (descriptionExpanded ? 'none' : 3) : 3}
                >
                  <Text variant='b4' color='default' tag='p'>
                    {description}
                  </Text>
                </LineClamp>
              )
            )}
            {useExpand &&
              (hasBulletList || hasDescription) &&
              (descriptionExpanded ||
                (hasBulletList
                  ? hasMultipleBullets
                  : isDescriptionTruncated)) && (
                <Button
                  variant='textOnly'
                  size='sm'
                  onClick={toggleExpanded}
                  className='self-start'
                  aria-expanded={descriptionExpanded}
                  aria-controls={descriptionId}
                >
                  {descriptionExpanded ? 'Read less' : 'Read More'}
                </Button>
              )}
            {!useExpand &&
              readMoreHref &&
              (hasBulletList || hasDescription) && (
                <Link
                  variant='primary'
                  underline='always'
                  href={readMoreHref}
                  size='sm'
                  weight='bold'
                  className='self-start'
                >
                  Read More
                </Link>
              )}

            {dataPointsSlice.length > 0 && (
              <Grid columns={3} gap='xs'>
                {dataPointsSlice.map((dp) => (
                  <PropertyValue
                    key={`${dp.property}-${dp.value}`}
                    propertyTitle={dp.property}
                    value={dp.value}
                  />
                ))}
              </Grid>
            )}

            {detailListSlice.length > 0 ? (
              <Flex
                direction='column'
                gap='sm'
                width='full'
                className={pinDetailList ? 'mt-auto' : undefined}
                data-degree-card-details
              >
                <List variant='unordered' bulletStyle='none'>
                  {detailListSlice.map((item, index) => (
                    <ListItem key={`${index}-${item.icon}-${item.label}`}>
                      <Flex direction='row' align='center' gap='xs'>
                        <Icon
                          icon={item.icon}
                          size='sm'
                          className='text-body'
                          aria-hidden
                        />
                        <Text variant='b4' color='default'>
                          {detailListItemsFormat === 'markdown'
                            ? parseMinimalMarkdown(item.label, {
                                linkClassName: MINIMAL_MARKDOWN_LINK_CLASS_NAME,
                              })
                            : item.label}
                        </Text>
                      </Flex>
                    </ListItem>
                  ))}
                </List>
                {price != null && String(price).trim() !== '' && (
                  <Price price={price} originalPrice={originalPrice} />
                )}
              </Flex>
            ) : (
              price != null &&
              String(price).trim() !== '' && (
                <Price price={price} originalPrice={originalPrice} />
              )
            )}
          </Flex>
        </CardContent>

        <CardFooter
          background='bg-white'
          padding='px-sm pb-sm'
          className={pinDetailList ? 'mt-0' : undefined}
        >
          <Flex
            data-degree-card-footer
            direction={isNarrowStackedFooter ? 'column' : 'row'}
            align={isNarrowStackedFooter ? 'stretch' : 'center'}
            justify={isNarrowStackedFooter ? 'start' : 'between'}
            gap='sm'
            childrenFill={footerLayout === 'row'}
            width={isNarrowStackedFooter ? 'full' : undefined}
            className={cn(
              isStackedFooter && 'w-full [&>*]:w-full',
              isStackUntilSmFooter &&
                'w-full [&>*]:w-full sm:flex-row sm:items-center sm:justify-between sm:[&>*]:w-auto sm:[&>*]:min-w-0 sm:[&>*]:flex-1',
            )}
          >
            {compare && (
              <CheckboxButton
                size='sm'
                label='Compare'
                checked={compareChecked}
                disabled={compareDisabled}
                onCheckedChange={
                  onCompareChange
                    ? (checked: boolean | 'indeterminate') =>
                        onCompareChange(checked === true)
                    : undefined
                }
              />
            )}
            {showCta && ctaHref?.trim() ? (
              <Button
                variant='primaryFill'
                size='sm'
                href={ctaHref}
                onClick={onCtaClick}
                className={DEGREE_CARD_CTA_TYPOGRAPHY}
              >
                {ctaLabel}
              </Button>
            ) : showCta ? (
              <Button
                type='button'
                variant='primaryFill'
                size='sm'
                onClick={onCtaClick}
                className={DEGREE_CARD_CTA_TYPOGRAPHY}
              >
                {ctaLabel}
              </Button>
            ) : null}
          </Flex>
        </CardFooter>

        {showTrustedPartnerStrip && (
          <Flex
            direction='row'
            align='center'
            gap='xs'
            width='full'
            className='px-md py-sm'
          >
            <Icon icon='lock' size='sm' className='text-body' aria-hidden />
            <Text variant='l2' color='default'>
              {trustedPartnerText}
            </Text>
          </Flex>
        )}
      </Shell>
    );
  },
);

DegreeCardDetailed.displayName = 'DegreeCardDetailed';
